import * as React from "react";

import { Alert } from "./Alert";
import { Button } from "./Button";
import { EmptyState } from "./EmptyState";
import { cn } from "./utils";
import { useTranslation } from "react-i18next";
import { translateUi } from "@i18n/config";

export const DEFAULT_LOADING_TIMEOUT_MS = 12_000;

export function useLoadingTimeout(
  isLoading: boolean,
  timeoutMs = DEFAULT_LOADING_TIMEOUT_MS,
) {
  const [timedOut, setTimedOut] = React.useState(false);

  React.useEffect(() => {
    if (!isLoading) {
      setTimedOut(false);
      return;
    }

    setTimedOut(false);
    const timer = window.setTimeout(() => setTimedOut(true), timeoutMs);
    return () => window.clearTimeout(timer);
  }, [isLoading, timeoutMs]);

  return timedOut;
}

export function getErrorMessage(
  error: unknown,
  fallback = translateUi(
    "theRequestFailedPleaseTryAgain",
    "The request failed. Please try again.",
  ),
) {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return fallback;
}

export function ErrorState({
  title,
  message,
  onRetry,
  retryLabel,
  retrying = false,
  className,
}: {
  title?: React.ReactNode;
  message?: React.ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  retrying?: boolean;
  className?: string;
}) {
  const { t: tUi } = useTranslation("ui");
  const resolvedTitle =
    title === undefined
      ? tUi("unableToLoadData", { defaultValue: "Unable to load data" })
      : title;
  const resolvedMessage =
    message === undefined
      ? tUi("pleaseRetryTheRequestIfTheProblemContinues", {
          defaultValue:
            "Please retry the request. If the problem continues, check the API status.",
        })
      : message;
  const resolvedRetryLabel =
    retryLabel === undefined
      ? tUi("retry", { defaultValue: "Retry" })
      : retryLabel;

  return (
    <Alert variant="error" title={resolvedTitle} className={className}>
      <div className="space-y-3">
        <div>{resolvedMessage}</div>
        {onRetry ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onRetry}
            loading={retrying}
          >
            {resolvedRetryLabel}
          </Button>
        ) : null}
      </div>
    </Alert>
  );
}

export function LoadingState({
  label,
  timeoutMs = DEFAULT_LOADING_TIMEOUT_MS,
  timeoutTitle,
  timeoutMessage,
  onRetry,
  retrying = false,
  className,
}: {
  label?: string;
  timeoutMs?: number;
  timeoutTitle?: React.ReactNode;
  timeoutMessage?: React.ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
  className?: string;
}) {
  const { t: tUi } = useTranslation("ui");
  const resolvedLabel =
    label === undefined
      ? tUi("loadingTitleCaseEllipsisCompact", { defaultValue: "Loading..." })
      : label;
  const resolvedTimeoutTitle =
    timeoutTitle === undefined
      ? tUi("stillLoading", { defaultValue: "Still loading" })
      : timeoutTitle;
  const resolvedTimeoutMessage =
    timeoutMessage === undefined
      ? tUi("thisIsTakingLongerThanExpectedRetryThe", {
          defaultValue:
            "This is taking longer than expected. Retry the request or reload the page.",
        })
      : timeoutMessage;
  const timedOut = useLoadingTimeout(true, timeoutMs);

  if (timedOut) {
    return (
      <ErrorState
        title={resolvedTimeoutTitle}
        message={resolvedTimeoutMessage}
        onRetry={onRetry}
        retrying={retrying}
        className={className}
      />
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn("text-sm text-muted-foreground", className)}
    >
      {resolvedLabel}
    </div>
  );
}

export function EmptyDataState({
  title,
  description,
  action,
  className,
}: {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  const { t: tUi } = useTranslation("ui");
  const resolvedTitle =
    title === undefined
      ? tUi("noDataYet", { defaultValue: "No data yet" })
      : title;
  return (
    <div className={className}>
      <EmptyState
        title={resolvedTitle}
        description={description}
        action={action}
      />
    </div>
  );
}
